/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { AppShell, rem, useMantineTheme, Group } from '@mantine/core';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { useSignOut } from '@fai/friendli-suite/features/auth/use-sign-out';
import Paths from '@fai/friendli-suite/features/routes/paths';
import { useOpenCanny } from '@fai/friendli-suite/shared/canny/use-open-feedback';
import DocsWidget from '@fai/friendli-suite/shared/mintlify/docs-widget';
import PillBackButton from '@fai/friendli-suite/views/common/button/back-button/pill';
import DocumentButton from '@fai/friendli-suite/views/layout/header/basic/product/document-button';
import WebsiteButton from '@fai/friendli-suite/views/layout/header/basic/product/website-button';
import SuiteLogo from '@fai/friendli-suite/views/layout/header/items/suite-logo';
import { ProfileComboBox } from '@fai/friendli-suite/views/layout/header/profile-combobox';

function SettingBasicLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { teamId } = useParams();
  const theme = useMantineTheme();

  const handleSignOut = useSignOut();
  const [handleFeedback] = useOpenCanny({
    postFieldCategory: 'feature-requests-feedback',
  });

  return (
    <AppShell h="100%" header={{ height: rem(64) }}>
      <AppShell.Header
        bg={theme.pf.color.fg.white}
        style={{
          borderBottom: `${rem(1)} solid ${theme.pf.color.border.neutral.default}`,
        }}
      >
        <Group h="100%" justify="space-between" px={theme.pf.layout.md} wrap="nowrap">
          <Group gap={theme.pf.layout.xs} wrap="nowrap">
            <SuiteLogo path={teamId && Paths.team(teamId).default} />

            <PillBackButton onClick={() => navigate(-1)}>{t('layout.back')}</PillBackButton>
          </Group>
          <Group gap={theme.pf.layout.xs} wrap="nowrap">
            <Group gap={theme.pf.layout['2xs']} wrap="nowrap">
              <DocsWidget />
              <WebsiteButton />
              <DocumentButton />
            </Group>

            <ProfileComboBox
              handleOnClickUserSettings={() => navigate(Paths.userSettings.default)}
              handleFeedback={handleFeedback}
              handleSignOut={handleSignOut}
            />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main>
        <Suspense>
          <Outlet />
        </Suspense>
      </AppShell.Main>
    </AppShell>
  );
}

export default SettingBasicLayout;
